/**
 * @license
 * Copyright 厦门乾元盛世科技有限公司 All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file.
 */


import { Input, Output, EventEmitter, ElementRef, Renderer, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { ComponentFactoryResolver, ComponentRef, ApplicationRef, Injector, Type, AfterViewInit, HostListener } from '@angular/core';
import { DomUtils } from '../util/dom.utils';
import { Popup } from './popup';
/**
 * @return {?}
 */
function getDocument() {
    return window.document;
}

const /** @type {?} */ defaultPopupAttributes = {
    'prefixCls': 'prefixCls',
    'destroyPopupOnHide': 'destroyPopupOnHide',
    'popupVisible': 'visible',
    'popupClass': 'class',
    'placement': 'placement',
    'action': 'action',
    'popupAnimation': 'animation',
    'popupTransitionName': 'transitionName',
    'mask': 'mask',
    'maskAnimation': 'maskAnimation',
    'maskTransitionName': 'maskTransitionName',
    'popupStyle': 'popupStyle',
    'zIndex': 'zIndex',
    'disabled': 'disabled'
};
/**
 * Abstract Trigger。
 * 参考 https://github.com/react-component/trigger
 */
export class Trigger implements OnInit, OnDestroy, OnChanges, AfterViewInit {

     prefixCls = 'ant-tooltip';

     destroyPopupOnHide = false;

     popupClass: string;
/**
 * 气泡框位置，可选 top left right bottom topLeft topRight bottomLeft bottomRight leftTop leftBottom rightTop rightBottom。
 * 默认值为top。
 */
placement = 'top';

     popupAlign = {};

     popupStyle = {};

     popupAnimation: any;

     popupTransitionName: string | Object;
/**
 * 是否不可用
 */
disabled = false;
/**
 * 是否已遮罩
 */
mask = false;
/**
 * 点击遮罩是否可关闭冒泡
 */
maskClosable = true;
/**
 * 遮罩动画名
 */
maskAnimation: string;
/**
 * 遮罩过渡效果
 */
maskTransitionName: string | Object;
/**
 * 鼠标移入后显示延迟时间（秒）
 */
mouseEnterDelay = 0;
/**
 * 鼠标移出后隐藏延迟时间（秒）
 */
mouseLeaveDelay = 0.1;
/**
 * 聚焦后显示延迟时间（秒）
 */
focusDelay = 0;
/**
 * 失焦后显示延迟时间（秒）
 */
blurDelay = 0.15;

     zIndex: number;


     action: string | string[] = [];
     showAction: string | string[] = [];
     hideAction: string | string[] = [];

    // How to create popup container
     autoMountPopup = false;
     autoDestroyPopup = true;
/**
 * 冒泡可视事件变更
 */
popupVisibleChange = new EventEmitter<boolean>();
/**
 * 记录聚焦时的开始时间
 */
private focusTime: number;
/**
 * 记录点击(MouseDown)的开始时间
 */
private preClickTime: number;
/**
 * 记录触摸(TouchStart)的开始时间
 */
private preTouchTime: number;
private clickOutsideHandler: Function;
private touchOutsideHandler: Function;
public popupComponentType: Type<Popup>;
private _popup_container: HTMLElement;
private _popupComponentRef: ComponentRef<Popup>;

    protected _popup: Popup;

    protected _popupVisible = false;
/**
 * 延迟计时器（显示或隐藏冒泡）
 */
private delayTimer: any;
/**
 * @param {?} _renderer
 * @param {?} _elementRef
 * @param {?} _componentFactoryResolver
 * @param {?} _appRef
 * @param {?} _injector
 */
constructor(
private _renderer: Renderer,
private _elementRef: ElementRef,
private _componentFactoryResolver: ComponentFactoryResolver,
private _appRef: ApplicationRef,
private _injector: Injector) {

    }
/**
 * @return {?}
 */
ngOnInit(): void {

    }
/**
 * @return {?}
 */
ngAfterViewInit(): void {
        if (this.autoMountPopup) {
            this.renderPopupComponent();
        }
    }
/**
 * @return {?}
 */
ngOnDestroy(): void {
        this.clearDelayTimer();
        this.clearOutsideHandler();

        if (this.autoDestroyPopup) {
            this.removePopupContainer();
        }
    }
/**
 * The underlying host native element
 * @return {?}
 */
getHostElement(): HTMLElement {
        return /** @type {?} */(( this._elementRef.nativeElement as HTMLElement));
    }
/**
 * @param {?} popupVisible
 * @return {?}
 */
set popupVisible(popupVisible: boolean) {
        this.setPopupVisible(popupVisible);
    }
/**
 * @return {?}
 */
get popupVisible(): boolean {
        return this._popupVisible;
    }
/**
 * @return {?}
 */
protected _getPopupContainer(): HTMLElement {
        if (!this._popup_container) {
            const /** @type {?} */ popupContainer = document.createElement('div');
            // Make sure default popup container will never cause scrollbar appearing
            // https://github.com/react-component/trigger/issues/41
            popupContainer.style.position = 'absolute';
            popupContainer.style.top = '0';
            popupContainer.style.left = '0';
            popupContainer.style.width = '100%';

            const /** @type {?} */ mountNode = this.getPopupContainer() || getDocument().body;
            mountNode.appendChild(popupContainer);
            this._popup_container = popupContainer;
        }

        return this._popup_container;
    }
/**
 * @return {?}
 */
getPopupContainer(): HTMLElement {
        return null;
    }
/**
 * @return {?}
 */
removePopupContainer(): void {
        if (this._popup_container) {
            // 销毁冒泡组件
            this._popupComponentRef.destroy();
            // 销毁冒泡组件的容器
            const /** @type {?} */ container = this._popup_container;
            container.parentNode.removeChild(container);
            this._popup_container = null;
        }
    }
/**
 * @return {?}
 */
renderPopupComponent(): void {
        if (this._popup) {
            return;
        }

        const /** @type {?} */ componentFactory = this._componentFactoryResolver.resolveComponentFactory(this.popupComponentType);
        const /** @type {?} */ componentRef: ComponentRef<Popup> = componentFactory.create(this._injector);
        this._appRef.attachView(componentRef.hostView);

        // At this point the component has been instantiated, so we move it to the location in the DOM
        // where we want it to be rendered.
        const /** @type {?} */ popupContainer: HTMLElement = this._getPopupContainer();
        popupContainer.appendChild(DomUtils.getComponentRootNode(componentRef));
        this._popupComponentRef = componentRef;
        this._popup = componentRef.instance;

        // 将Trigger的属性赋值给Popup
        for (const /** @type {?} */ name in defaultPopupAttributes) {
            if (defaultPopupAttributes.hasOwnProperty(name)) {
                this.setPopupAttribute(defaultPopupAttributes[name], this[name]);
            }
        }

        // 将Popup组件(元素)与Trigger组件(元素)关联起来
        this.registerTriggerElement(this.getHostElement());
        if (this.isMouseEnterToShow()) {
            this._popup.registerOnMouseEnter(this.onPopupMouseEnter.bind(this));
        }
        if (this.isMouseLeaveToHide()) {
            this._popup.registerOnMouseLeave(this.onPopupMouseLeave.bind(this));
        }
        this.afterRenderPopupComponent();
    }
/**
 * @return {?}
 */
afterRenderPopupComponent(): void {
        // can be overrided by subclasss
    }
/**
 * @param {?} triggerElement
 * @return {?}
 */
registerTriggerElement(triggerElement: HTMLElement): void {
        this._popup.registerTriggerElement(triggerElement);
    }
/**
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
setPopupAttribute(name: string, value: any): void {
        if (this._popup) {
            this._popup[name] = value;
        }
    }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges): void {
        if (this._popup) {
            for (const /** @type {?} */ name in defaultPopupAttributes) {
                if (defaultPopupAttributes.hasOwnProperty(name)) {
                    const /** @type {?} */ thisKey = defaultPopupAttributes[name];
                    const /** @type {?} */ change = changes[thisKey];
                    if (change) {
                        this.setPopupAttribute(name, change.currentValue);
                    }
                }
            }
        }
    }
/**
 * @param {?} popupComponentType
 * @return {?}
 */
public setPopupComponentType(popupComponentType: Type<Popup>): void {
        this.popupComponentType = popupComponentType;
    }
/**
 * @param {?} event
 * @return {?}
 */
onClick(event: MouseEvent): void {
        if (this.isClickToHide() || this.isClickToShow()) {
            // focus will trigger click
            if (this.focusTime) {
                let /** @type {?} */ preTime;
                if (this.preClickTime && this.preTouchTime) {
                    preTime = Math.min(this.preClickTime, this.preTouchTime);
                } else if (this.preClickTime) {
                    preTime = this.preClickTime;
                } else if (this.preTouchTime) {
                    preTime = this.preTouchTime;
                }
                if (Math.abs(preTime - this.focusTime) < 20) {
                    return;
                }
                this.focusTime = 0;
            }
            this.preClickTime = 0;
            this.preTouchTime = 0;
            event.preventDefault();

            const /** @type {?} */ nextVisible = !this.popupVisible;
            if (this.isClickToHide() && !nextVisible || nextVisible && this.isClickToShow()) {
                this.setPopupVisible(!this.popupVisible);
            }
        }
    }
/**
 * @param {?} e
 * @return {?}
 */
onMouseDown(e: MouseEvent): void {
        if (this.isClickToHide() || this.isClickToShow()) {
            this.preClickTime = Date.now();
        }
    }
/**
 * @param {?} e
 * @return {?}
 */
onTouchStart(e: MouseEvent): void {
        if (this.isClickToHide() || this.isClickToShow()) {
            this.preTouchTime = Date.now();
        }
    }
/**
 * @param {?} e
 * @return {?}
 */
onFocus(e: any): void {
        if (this.isFocusToShow() || this.isBlurToHide()) {
            // incase focusin and focusout
            this.clearDelayTimer();
            if (this.isFocusToShow()) {
                this.focusTime = Date.now();
                this.delaySetPopupVisible(true, this.focusDelay);
            }
        }
    }
/**
 * @param {?} e
 * @return {?}
 */
onBlur(e: MouseEvent): void {
        if (this.isFocusToShow() || this.isBlurToHide()) {
            this.clearDelayTimer();
            if (this.isBlurToHide()) {
                this.delaySetPopupVisible(false, this.blurDelay);
            }
        }
    }
/**
 * @param {?} e
 * @return {?}
 */
onMouseEnter(e: MouseEvent): void {
        if (this.isMouseEnterToShow()) {
            this.delaySetPopupVisible(true, this.mouseEnterDelay);
        }
    }
/**
 * @param {?} e
 * @return {?}
 */
onMouseLeave(e: MouseEvent): void {
        if (this.isMouseLeaveToHide()) {
            this.delaySetPopupVisible(false, this.mouseLeaveDelay);
        }
    }
/**
 * @return {?}
 */
onPopupMouseEnter(): void {
        this.clearDelayTimer();
    }
/**
 * @param {?} e
 * @return {?}
 */
onPopupMouseLeave(e: MouseEvent): void {
        // https://github.com/react-component/trigger/pull/13
        // react bug?
        if (e.relatedTarget && !e.relatedTarget['setTimeout'] &&
            this._popup &&
            DomUtils.contains(this._popup.getHostElement(), /** @type {?} */(( e.relatedTarget as Node)))) {
            return;
        }
        this.delaySetPopupVisible(false, this.mouseLeaveDelay);
    }
/**
 * @param {?} event
 * @return {?}
 */
onDocumentClick(event: MouseEvent): void {
        if (this.mask && !this.maskClosable) {
            return;
        }
        const /** @type {?} */ target = /** @type {?} */(( event.target as Node));
        const /** @type {?} */ root = this.getHostElement();
        const /** @type {?} */ popupEl = this.getPopupElement();
        if (!DomUtils.contains(root, target) && !DomUtils.contains(popupEl, target)) {
            this.close();
        }
    }
/**
 * @return {?}
 */
getPopupElement(): HTMLElement {
        return this._popup && this._popup.getHostElement() || null;
    }
/**
 * @return {?}
 */
isPopupVisible(): boolean {
        return this._popupVisible;
    }
/**
 * @param {?} popupVisible
 * @return {?}
 */
setPopupVisible(popupVisible: boolean): void {
        if (this.disabled) {
            return;
        }

        this.clearDelayTimer();
        if (this._popupVisible !== popupVisible) {
            this._popupVisible = popupVisible;

            // We must listen to `mousedown` or `touchstart`, edge case:
            // https://github.com/ant-design/ant-design/issues/5804
            // https://github.com/react-component/calendar/issues/250
            // https://github.com/react-component/trigger/issues/50
            if (popupVisible) {
                if (!this.clickOutsideHandler && this.isClickToHide()) {
                    this.clickOutsideHandler = this._renderer.listen('document', 'mousedown', this.onDocumentClick.bind(this));
                }
                // always hide on mobile
                if (!this.touchOutsideHandler) {
                    this.touchOutsideHandler = this._renderer.listen('document', 'touchstart', this.onDocumentClick.bind(this));
                }
            }
            if (!popupVisible) {
                this.clearOutsideHandler();
            }

            if (popupVisible) {
                this.renderPopupComponent();
            }
            if (this._popup) {
                this._popup.visible = popupVisible;
            }
            // this.forcePopupAlign(); // 强制调整位置

            this.afterPopupVisibleChange(popupVisible);
            this.popupVisibleChange.emit(popupVisible);
        }
    }
/**
 * @param {?} popupVisible
 * @return {?}
 */
afterPopupVisibleChange(popupVisible: boolean): void {
        // do nothing here, but can be overrided by subclass
    }
/**
 * 显示或者隐藏冒泡
 * 
 * @param {?} visible true-显示冒泡，false-隐藏冒泡
 * @param {?} delayS 延迟时间（单位：秒）
 * @return {?}
 */
delaySetPopupVisible(visible: boolean, delayS: number): void {
        const /** @type {?} */ delay: number = delayS * 1000;
        this.clearDelayTimer();
        if (delay) {
            this.delayTimer = setTimeout(() => {
                this.setPopupVisible(visible);
                this.clearDelayTimer();
            }, delay);
        } else {
            this.setPopupVisible(visible);
        }
    }
/**
 * @return {?}
 */
clearDelayTimer() {
        if (this.delayTimer) {
            clearTimeout(this.delayTimer);
            this.delayTimer = null;
        }
    }
/**
 * @return {?}
 */
clearOutsideHandler() {
        if (this.clickOutsideHandler) {
            this.clickOutsideHandler(); // Removes "listen" listener
            this.clickOutsideHandler = null;
        }

        if (this.touchOutsideHandler) {
            this.touchOutsideHandler(); // Removes "listen" listener
            this.touchOutsideHandler = null;
        }
    }
/**
 * @return {?}
 */
isClickToShow() {
        return this.action.indexOf('click') !== -1 || this.showAction.indexOf('click') !== -1;
    }
/**
 * @return {?}
 */
isClickToHide() {
        return this.action.indexOf('click') !== -1 || this.hideAction.indexOf('click') !== -1;
    }
/**
 * @return {?}
 */
isMouseEnterToShow() {
        return this.action.indexOf('hover') !== -1 || this.showAction.indexOf('mouseEnter') !== -1;
    }
/**
 * @return {?}
 */
isMouseLeaveToHide() {
        return this.action.indexOf('hover') !== -1 || this.hideAction.indexOf('mouseLeave') !== -1;
    }
/**
 * @return {?}
 */
isFocusToShow() {
        return this.action.indexOf('focus') !== -1 || this.showAction.indexOf('focus') !== -1;
    }
/**
 * @return {?}
 */
isBlurToHide() {
        return this.action.indexOf('focus') !== -1 || this.hideAction.indexOf('blur') !== -1;
    }
/**
 * @return {?}
 */
forcePopupAlign() {
        if (this.isPopupVisible() && this._popup) {
            this._popup.forceAlign();
        }
    }
/**
 * @return {?}
 */
close() {
        this.setPopupVisible(false);
    }

static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'prefixCls': [{ type: Input },],
'destroyPopupOnHide': [{ type: Input },],
'popupClass': [{ type: Input },],
'placement': [{ type: Input },],
'popupAlign': [{ type: Input },],
'popupStyle': [{ type: Input },],
'popupAnimation': [{ type: Input },],
'popupTransitionName': [{ type: Input },],
'disabled': [{ type: Input },],
'mask': [{ type: Input },],
'maskClosable': [{ type: Input },],
'maskAnimation': [{ type: Input },],
'maskTransitionName': [{ type: Input },],
'mouseEnterDelay': [{ type: Input },],
'mouseLeaveDelay': [{ type: Input },],
'focusDelay': [{ type: Input },],
'blurDelay': [{ type: Input },],
'zIndex': [{ type: Input },],
'action': [{ type: Input },],
'showAction': [{ type: Input },],
'hideAction': [{ type: Input },],
'autoMountPopup': [{ type: Input },],
'autoDestroyPopup': [{ type: Input },],
'popupVisibleChange': [{ type: Output },],
'popupVisible': [{ type: Input },],
'onClick': [{ type: HostListener, args: ['click', ['$event'], ] },],
'onMouseDown': [{ type: HostListener, args: ['mousedown', ['$event'], ] },],
'onTouchStart': [{ type: HostListener, args: ['touchstart', ['$event'], ] },],
'onFocus': [{ type: HostListener, args: ['focus', ['$event'], ] },],
'onBlur': [{ type: HostListener, args: ['blur', ['$event'], ] },],
'onMouseEnter': [{ type: HostListener, args: ['mouseenter', ['$event'], ] },],
'onMouseLeave': [{ type: HostListener, args: ['mouseleave', ['$event'], ] },],
};
}

function Trigger_tsickle_Closure_declarations() {
/** @type {?} */
Trigger.propDecorators;
/** @type {?} */
Trigger.prototype.prefixCls;
/** @type {?} */
Trigger.prototype.destroyPopupOnHide;
/** @type {?} */
Trigger.prototype.popupClass;
/**
 * 气泡框位置，可选 top left right bottom topLeft topRight bottomLeft bottomRight leftTop leftBottom rightTop rightBottom。
 * 默认值为top。
 * @type {?}
 */
Trigger.prototype.placement;
/** @type {?} */
Trigger.prototype.popupAlign;
/** @type {?} */
Trigger.prototype.popupStyle;
/** @type {?} */
Trigger.prototype.popupAnimation;
/** @type {?} */
Trigger.prototype.popupTransitionName;
/**
 * 是否不可用
 * @type {?}
 */
Trigger.prototype.disabled;
/**
 * 是否已遮罩
 * @type {?}
 */
Trigger.prototype.mask;
/**
 * 点击遮罩是否可关闭冒泡
 * @type {?}
 */
Trigger.prototype.maskClosable;
/**
 * 遮罩动画名
 * @type {?}
 */
Trigger.prototype.maskAnimation;
/**
 * 遮罩过渡效果
 * @type {?}
 */
Trigger.prototype.maskTransitionName;
/**
 * 鼠标移入后显示延迟时间（秒）
 * @type {?}
 */
Trigger.prototype.mouseEnterDelay;
/**
 * 鼠标移出后隐藏延迟时间（秒）
 * @type {?}
 */
Trigger.prototype.mouseLeaveDelay;
/**
 * 聚焦后显示延迟时间（秒）
 * @type {?}
 */
Trigger.prototype.focusDelay;
/**
 * 失焦后显示延迟时间（秒）
 * @type {?}
 */
Trigger.prototype.blurDelay;
/** @type {?} */
Trigger.prototype.zIndex;
/** @type {?} */
Trigger.prototype.action;
/** @type {?} */
Trigger.prototype.showAction;
/** @type {?} */
Trigger.prototype.hideAction;
/** @type {?} */
Trigger.prototype.autoMountPopup;
/** @type {?} */
Trigger.prototype.autoDestroyPopup;
/**
 * 冒泡可视事件变更
 * @type {?}
 */
Trigger.prototype.popupVisibleChange;
/**
 * 记录聚焦时的开始时间
 * @type {?}
 */
Trigger.prototype.focusTime;
/**
 * 记录点击(MouseDown)的开始时间
 * @type {?}
 */
Trigger.prototype.preClickTime;
/**
 * 记录触摸(TouchStart)的开始时间
 * @type {?}
 */
Trigger.prototype.preTouchTime;
/** @type {?} */
Trigger.prototype.clickOutsideHandler;
/** @type {?} */
Trigger.prototype.touchOutsideHandler;
/** @type {?} */
Trigger.prototype.popupComponentType;
/** @type {?} */
Trigger.prototype._popup_container;
/** @type {?} */
Trigger.prototype._popupComponentRef;
/** @type {?} */
Trigger.prototype._popup;
/** @type {?} */
Trigger.prototype._popupVisible;
/**
 * 延迟计时器（显示或隐藏冒泡）
 * @type {?}
 */
Trigger.prototype.delayTimer;
/** @type {?} */
Trigger.prototype._renderer;
/** @type {?} */
Trigger.prototype._elementRef;
/** @type {?} */
Trigger.prototype._componentFactoryResolver;
/** @type {?} */
Trigger.prototype._appRef;
/** @type {?} */
Trigger.prototype._injector;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
